import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Truck, Clock, Timer } from "lucide-react";
import axios from "axios";
import { useSettings } from "../context/SettingsContext";
import { toast } from "react-toastify";

const ServiceSelectionModal = ({ onConfirm, onClose }) => {
  const [serviceType, setServiceType] = useState(null);
  const [orderTime, setOrderTime] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [timeSlots, setTimeSlots] = useState([]);
  const { settings, loading } = useSettings();
  // 🔹 Convert yyyy-mm-dd → dd-mm-yyyy
  const formatDateDMY = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}-${m}-${y}`;
  };

  // 🔹 Fetch slots API
  const fetchTimeSlots = async (date, type) => {
    try {
      const payload = {
        date: formatDateDMY(date),
        deliver_type: type === "delivery" ? "0" : "1",
      };
      const { data } = await axios.post(
        `${window.BASE_API_URL}gettimes`,
        payload
      );
      if (Array.isArray(data)) {
        setTimeSlots(data);
      } else {
        setTimeSlots([]);
      }
    } catch (err) {
      console.error("Failed to fetch time slots:", err);
      setTimeSlots([]);
    }
  };

  // 🔹 Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("serviceSelection");
    if (saved) {
      const parsed = JSON.parse(saved);
      setServiceType(parsed.orderType || null);
      setOrderTime(parsed.orderTiming || null);
      setDate(parsed.scheduleDate || "");
      setTime(parsed.scheduleTime || "");
    }
  }, []);

  const today = new Date().toISOString().split("T")[0];

// Create a copy of today's date for max calculation
const maxDateObj = new Date();
maxDateObj.setDate(maxDateObj.getDate() + (settings?.delivery_day || 0));

const maxDate = maxDateObj.toISOString().split("T")[0];

  // 🔹 Watch changes for schedule slots
  useEffect(() => {
    if (orderTime === "later" && date && serviceType) {
      fetchTimeSlots(date, serviceType);
    } else {
      setTimeSlots([]);
    }
  }, [date, orderTime, serviceType]);

  const handleConfirm = () => {
    if (!serviceType || !orderTime) {
      toast.error("Please complete all selections");
      return;
    }
    if (orderTime === "later" && (!date || !time)) {
      toast.error("Please select date and time for schedule");
      return;
    }

    const data = {
      orderType: serviceType,
      orderTiming: orderTime,
      scheduleDate: orderTime === "later" ? date : null,
      scheduleTime: orderTime === "later" ? time : null,
    };

    
  localStorage.setItem("orderType",serviceType);
  localStorage.setItem("orderTiming",orderTime);
  localStorage.setItem("scheduleDate",date);
  localStorage.setItem("scheduleTime",time);


    // 🔹 Save to localStorage
    localStorage.setItem("serviceSelection", JSON.stringify(data));

    onConfirm(data);
    onClose(); // close modal after confirm
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 loginmod">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl shadow-2xl p-8 w-[90%] max-w-md text-center relative"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-black"
        >
          ✕
        </button>

        <h2 className="text-2xl font-bold mb-6">Choose Your Service</h2>

        {/* Delivery Type */}
        <div className="flex justify-center gap-6 mb-6">
          <button
            onClick={() => setServiceType("pickup")}
            className={`flex flex-col items-center gap-2 p-6 rounded-xl border-2 transition-all 
            ${serviceType === "pickup" ? "border-black bg-gray-100" : "border-gray-200 hover:border-black"}`}
          >
            <Home size={32} />
            <span className="font-medium">Pickup</span>
          </button>
          <button
            onClick={() => setServiceType("delivery")}
            className={`flex flex-col items-center gap-2 p-6 rounded-xl border-2 transition-all 
            ${serviceType === "delivery" ? "border-black bg-gray-100" : "border-gray-200 hover:border-black"}`}
          >
            <Truck size={32} />
            <span className="font-medium">Delivery</span>
          </button>
        </div>

        {/* Order Time */}
        {serviceType && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="space-y-6"
            >
              <h3 className="text-lg font-semibold">
                When would you like your order?
              </h3>
              <div className="flex justify-center gap-6">
                <button
                  onClick={() => setOrderTime("now")}
                  className={`flex flex-col items-center gap-2 p-6 rounded-xl border-2 transition-all 
                  ${orderTime === "now" ? "border-black bg-gray-100" : "border-gray-200 hover:border-black"}`}
                >
                  <Timer size={32} />
                  <span className="font-medium">Now</span>
                </button>
                <button
  onClick={() => {
    setOrderTime("later");
    setDate(today);   // ✅ default today
    setTime("");      // reset time
  }}
  className={`flex flex-col items-center gap-2 p-6 rounded-xl border-2 transition-all 
  ${orderTime === "later" ? "border-black bg-gray-100" : "border-gray-200 hover:border-black"}`}
>
  <Clock size={32} />
  <span className="font-medium">Schedule</span>
</button>

              </div>

              {/* Schedule Inputs */}
              {orderTime === "later" && (
                
                <div className="flex flex-col gap-4 items-center">
                  <input
  type="date"
  value={date || today}
  onChange={(e) => setDate(e.target.value)}
  min={today}
  max={maxDate}
  className="w-full h-12 px-4 rounded-xl border border-gray-300 
             text-gray-800 text-base bg-white
             focus:outline-none focus:ring-2 focus:ring-black"
/>


                  {timeSlots.length > 0 ? (
                    <select
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="border rounded-lg p-2 w-full"
                    >
                      <option value="">Select Time</option>
                      {timeSlots.map((slot, i) => (
                        <option key={i} value={slot}>
                          {slot.substring(0, 5)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value=""
                      onChange={(e) => setTime(e.target.value)}
                      className="border rounded-lg p-2 w-full"
                    >
                      <option value="">Select Time</option>
                     
                    </select>
                  )}
                </div>
              )}
{serviceType && orderTime &&
              <button
                onClick={handleConfirm}
                
style={{
  background: settings?.cart_btn_color || "#000000ff",
   color: settings?.cart_btn_text_color || "#ffffff",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_hover || "#f59e0b";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_color || "#ffffff";
}}

                className="mt-6 w-full bg-black text-white py-3 rounded-xl font-semibold hover:bg-gray-900 transition-all"
              >
                Save & Continue
              </button> }
            </motion.div>
          </AnimatePresence>
        )}
      </motion.div>
    </div>
  );
};

export default ServiceSelectionModal;
